'use client';

import React, { useState } from 'react';
import Link from 'next/link';

type Tier = 'student' | 'undergraduate' | 'premium' | 'seniors';
type Provider = 'evc' | 'edahab';

interface Plan {
  tier: Tier;
  label: string;
  price: number;
  description: string;
  features: string[];
  gradient: string;
  badge?: string;
}

const PLANS: Plan[] = [
  {
    tier: 'student',
    label: 'Student',
    price: 8500,
    description: 'For students starting their growth journey',
    features: [
      'Voice chat with Rafeeq AI',
      'Daily motivational videos',
      'Habit tracking & streaks',
      'Prayer times & Qibla',
      '5-language support',
    ],
    gradient: 'from-emerald-500 to-teal-600',
  },
  {
    tier: 'undergraduate',
    label: 'Undergraduate',
    price: 11500,
    description: 'For undergraduates building their future',
    features: [
      'Everything in Student',
      'Academic Research Mode',
      'Extended conversation history',
      'Priority AI responses',
      'Weekly growth analytics',
    ],
    gradient: 'from-blue-500 to-indigo-600',
    badge: 'Popular',
  },
  {
    tier: 'premium',
    label: 'Premium',
    price: 17000,
    description: 'For professionals & entrepreneurs',
    features: [
      'Everything in Undergraduate',
      'Career Launchpad access',
      'Advanced voice integration',
      'Unlimited AI coaching',
      'Business goal tracking',
    ],
    gradient: 'from-amber-500 to-orange-600',
  },
  {
    tier: 'seniors',
    label: 'Seniors',
    price: 14000,
    description: 'Exclusive ibada guidance for elders',
    features: [
      'Personalised ibada guidance',
      'Private Scholar Q&A (ask any Sheikh)',
      'Record or type your question',
      'Answers delivered privately to you',
      'Curated Deen content for seniors',
      'Prayer & Dhikr reminders',
    ],
    gradient: 'from-purple-600 to-violet-700',
    badge: 'Seniors Only',
  },
];

type CheckoutStep = 'select-provider' | 'form' | 'waiting' | 'success' | 'failed';

interface SomaliaCheckoutProps {
  plan: Plan;
  onSuccess: () => void;
  onCancel: () => void;
}

const PROVIDERS: { id: Provider; name: string; shortName: string; icon: string; color: string; description: string; placeholder: string; prefix: string; prefixDisplay: string }[] = [
  {
    id: 'evc',
    name: 'Hormuud EVC Plus',
    shortName: 'EVC Plus',
    icon: '📱',
    color: 'from-blue-600 to-blue-700',
    description: 'Pay with your Hormuud EVC Plus wallet',
    placeholder: '061X XXX XXX',
    prefix: '252',
    prefixDisplay: '🇸🇴 +252',
  },
  {
    id: 'edahab',
    name: 'Somtel eDahab',
    shortName: 'eDahab',
    icon: '💳',
    color: 'from-orange-500 to-orange-600',
    description: 'Pay with your Somtel eDahab wallet',
    placeholder: '063X XXX XXX',
    prefix: '252',
    prefixDisplay: '🇸🇴 +252',
  },
];

function SomaliaCheckout({ plan, onSuccess, onCancel }: SomaliaCheckoutProps) {
  const [step, setStep] = useState<CheckoutStep>('select-provider');
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const provider = PROVIDERS.find((p) => p.id === selectedProvider);

  const handleSelectProvider = (id: Provider) => {
    setSelectedProvider(id);
    setPhone('');
    setError('');
    setStep('form');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!phone.trim()) {
      setError('Please enter your mobile wallet phone number.');
      return;
    }

    const cleaned = phone.replace(/\s+/g, '').replace(/^0/, '252');
    if (!/^252(61|63|65|66|68)\d{7}$/.test(cleaned)) {
      setError('Please enter a valid Somali mobile number (e.g. 061X XXX XXX or 063X XXX XXX).');
      return;
    }

    setLoading(true);
    setStep('waiting');

    try {
      const response = await fetch('/api/payments/somalia', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleaned, amount: plan.price, tier: plan.tier, provider: selectedProvider }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Payment initiation failed. Please try again.');
        setStep('failed');
      } else {
        setStep('success');
        onSuccess();
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
      setStep('failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Plan header */}
      <div className={`bg-gradient-to-r ${plan.gradient} rounded-2xl p-6 mb-6 text-white`}>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <span className="text-xl">📱</span>
          </div>
          <div>
            <p className="text-white/80 text-sm font-medium">Mobile Wallet Payment · Somalia</p>
            <p className="text-white font-700 text-lg">{plan.label} Plan</p>
          </div>
        </div>
        <div className="mt-4 flex items-baseline gap-1">
          <span className="text-3xl font-800">SOS {plan.price.toLocaleString()}</span>
          <span className="text-white/70 text-sm">/month</span>
        </div>
      </div>

      {/* Step: Select Provider */}
      {step === 'select-provider' && (
        <div className="space-y-4">
          <p className="text-sm font-600 text-foreground mb-3">Choose your mobile wallet provider:</p>
          {PROVIDERS.map((p) => (
            <button
              key={p.id}
              onClick={() => handleSelectProvider(p.id)}
              className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 border-border bg-card hover:border-primary hover:shadow-md transition-all duration-200 text-left group`}
            >
              <div className={`w-12 h-12 bg-gradient-to-br ${p.color} rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0`}>
                {p.icon}
              </div>
              <div className="flex-1">
                <p className="font-700 text-foreground text-sm group-hover:text-primary transition-colors">{p.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>
              </div>
              <svg className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ))}
          <button
            type="button"
            onClick={onCancel}
            className="w-full py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors mt-2"
          >
            ← Back to plans
          </button>
        </div>
      )}

      {/* Step: Form */}
      {step === 'form' && provider && (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Provider badge */}
          <div className={`flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r ${provider.color} text-white`}>
            <span className="text-xl">{provider.icon}</span>
            <div>
              <p className="font-700 text-sm">{provider.name}</p>
              <p className="text-white/70 text-xs">{provider.description}</p>
            </div>
            <button
              type="button"
              onClick={() => setStep('select-provider')}
              className="ml-auto text-white/70 hover:text-white text-xs underline"
            >
              Change
            </button>
          </div>

          <div>
            <label className="block text-sm font-600 text-foreground mb-2">
              {provider.name} Phone Number
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <span className="text-muted-foreground text-sm font-medium">{provider.prefixDisplay}</span>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={provider.placeholder}
                className="w-full pl-24 pr-4 py-3 border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                maxLength={13}
                autoComplete="tel"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              {provider.id === 'evc' ?'Hormuud EVC Plus number (061X or 065X)' :'Somtel eDahab number (063X or 068X)'}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
              <span className="text-red-500 text-sm mt-0.5">⚠️</span>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="bg-muted/50 rounded-xl p-4 space-y-2">
            <p className="text-xs font-600 text-foreground">How it works:</p>
            <div className="space-y-1.5">
              {[
                `Enter your ${provider.shortName} number`,
                'You\'ll receive a payment prompt on your phone',
                'Approve the payment with your PIN',
                'Your subscription activates instantly',
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-xs font-700 text-primary bg-primary/10 rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-xs text-muted-foreground">{s}</p>
                </div>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3.5 px-4 bg-gradient-to-r ${plan.gradient} text-white font-600 rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60`}
          >
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Processing…
              </>
            ) : (
              <>
                <span>{provider.icon}</span>
                Pay SOS {plan.price.toLocaleString()} via {provider.shortName}
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => setStep('select-provider')}
            className="w-full py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back to provider selection
          </button>
        </form>
      )}

      {/* Step: Waiting */}
      {step === 'waiting' && (
        <div className="text-center space-y-6 py-8">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-primary animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-700 text-foreground mb-2">Waiting for payment…</h3>
            <p className="text-sm text-muted-foreground">Check your phone and approve the {provider?.shortName} payment prompt.</p>
          </div>
        </div>
      )}

      {/* Step: Success */}
      {step === 'success' && (
        <div className="text-center space-y-6 py-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-700 text-foreground mb-2">Payment Confirmed! 🎉</h3>
            <p className="text-sm text-muted-foreground">Your Rafeeq subscription is now active.</p>
          </div>
          <Link
            href="/dashboard"
            className={`block w-full py-3 px-6 bg-gradient-to-r ${plan.gradient} text-white font-600 rounded-xl hover:opacity-90 transition-opacity text-center`}
          >
            Go to Dashboard →
          </Link>
        </div>
      )}

      {/* Step: Failed */}
      {step === 'failed' && (
        <div className="text-center space-y-6 py-8">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-700 text-foreground mb-2">Payment Failed</h3>
            <p className="text-sm text-muted-foreground">The payment was not completed. Please try again.</p>
          </div>
          <button
            onClick={() => { setStep('select-provider'); setError(''); }}
            className={`block w-full py-3 px-6 bg-gradient-to-r ${plan.gradient} text-white font-600 rounded-xl hover:opacity-90 transition-opacity text-center`}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}

export default function SomaliaPricingPage() {
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const handleSuccess = () => {
    setPaymentSuccess(true);
    setSelectedPlan(null);
  };

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-12 h-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-800 text-foreground mb-2">Welcome to Rafeeq Premium! 🎉</h1>
            <p className="text-muted-foreground">Your subscription is now active. Start your growth journey.</p>
          </div>
          <Link
            href="/"
            className="block w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-600 rounded-xl hover:opacity-90 transition-opacity text-center"
          >
            Go to Dashboard →
          </Link>
        </div>
      </div>
    );
  }

  if (selectedPlan) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <button
            onClick={() => setSelectedPlan(null)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to plans
          </button>
          <SomaliaCheckout
            plan={selectedPlan}
            onSuccess={handleSuccess}
            onCancel={() => setSelectedPlan(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-700 px-4 py-1.5 rounded-full text-sm font-600 mb-4">
            <span>🇸🇴</span>
            <span>Pay with EVC Plus or eDahab · Somalia</span>
          </div>
          <h1 className="text-3xl font-800 text-foreground mb-3">Choose Your Rafeeq Plan</h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            Unlock AI-powered coaching, voice chat, and daily growth tools. Pay securely with Hormuud EVC Plus or Somtel eDahab — no card needed.
          </p>
        </div>

        {/* Provider badges */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {PROVIDERS.map((p) => (
            <div
              key={p.id}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r ${p.color} text-white text-sm font-600 shadow-sm`}
            >
              <span>{p.icon}</span>
              <span>{p.name}</span>
            </div>
          ))}
        </div>

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {PLANS.map((plan) => (
            <div
              key={plan.tier}
              className={`relative bg-card border rounded-2xl overflow-hidden transition-all hover:shadow-lg ${
                plan.badge ? 'border-blue-300 shadow-blue-100 shadow-md' : 'border-border'
              }`}
            >
              {plan.badge && (
                <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-700 px-3 py-1 rounded-bl-xl">
                  {plan.badge}
                </div>
              )}

              <div className={`bg-gradient-to-r ${plan.gradient} p-6 text-white`}>
                <h3 className="text-lg font-700 mb-1">{plan.label}</h3>
                <p className="text-white/70 text-xs mb-4">{plan.description}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-800">SOS {plan.price.toLocaleString()}</span>
                  <span className="text-white/70 text-sm">/month</span>
                </div>
              </div>

              <div className="p-6">
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setSelectedPlan(plan)}
                  className={`w-full py-3 px-4 bg-gradient-to-r ${plan.gradient} text-white font-600 text-sm rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
                >
                  <span>📲</span>
                  Pay with EVC Plus or eDahab
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Trust indicators */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            {[
              { icon: '🔒', title: 'Secure Payments', desc: 'Powered by Hormuud EVC Plus & Somtel eDahab' },
              { icon: '⚡', title: 'Instant Activation', desc: 'Subscription active within seconds' },
              { icon: '📞', title: 'No Card Needed', desc: 'Pay directly from your mobile wallet' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <span className="text-2xl">{item.icon}</span>
                <p className="text-sm font-600 text-foreground">{item.title}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Prices shown in Somali Shillings (SOS). Subscriptions renew monthly. Cancel anytime.
        </p>
      </div>
    </div>
  );
}
